import { useState, useEffect } from "react";
import API_BASE_URL from "../config";
import { useTheme } from "../context/ThemeContext";
import Pagination from "./Pagination";
import SearchIcon from "@mui/icons-material/Search";
import { getUserRole } from "../utils/Auth";

const CustomerStatistics = () => {
  const { darkMode } = useTheme();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const customersPerPage = 15;
  const [isAdmin, setIsAdmin] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");

  useEffect(() => {
    const role = getUserRole();
    setIsAdmin(role === "Admin");

    if (role === "Admin") {
      const fetchAgents = async () => {
        try {
          const token = localStorage.getItem("token");
          if (!token) {
            console.warn("Nincs token, kihagyjuk az API hívást.");
            return;
          }

          const response = await fetch(`${API_BASE_URL}/api/users`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (response.status === 403) {
            console.warn("Nincs admin jogosultság az ügynökök lekérésére.");
            return;
          }

          if (!response.ok) {
            throw new Error("Hiba az ügynökök betöltésekor.");
          }

          const data = await response.json();
          setAgents(data);
        } catch (err) {
          console.error("Hiba a felhasználók lekérésekor:", err);
        }
      };

      fetchAgents();
    }
  }, []);

  useEffect(() => {
    const fetchCustomerRankings = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_BASE_URL}/api/customers/rankings`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error("Eroare la încărcarea statisticilor clienților.");
        }

        const data = await response.json();
        setCustomers(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerRankings();
  }, []);

  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch = customer.customerName
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    const matchesAgent = selectedAgent
      ? Number(customer.agentId) === Number(selectedAgent) // 🔥 Alakítsuk számmá
      : true;

    return matchesSearch && matchesAgent;
  });

  const indexOfLastCustomer = currentPage * customersPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - customersPerPage;
  const currentCustomers = filteredCustomers.slice(
    indexOfFirstCustomer,
    indexOfLastCustomer
  );
  const totalPages = Math.ceil(filteredCustomers.length / customersPerPage);

  return (
    <div
      className={`min-h-screen p-6 ${
        darkMode ? "bg-gray-900 text-white" : "bg-gray-200 text-black"
      }`}
    >
      {/* 🔥 Titlu + Căutare + Filtrare pe un singur rând */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold">Statisticile clienților</h2>

        {/* 🔍 Căutare + Filtrare agent */}
        <div className="flex gap-4 items-center">
          {/* 🔥 Filtrare după agent (doar pentru admin) */}
          {isAdmin && (
            <select
              className={`p-2 border rounded-3xl ${
                darkMode
                  ? "bg-gray-800 text-white border-gray-600"
                  : "bg-white text-black border-gray-400"
              }`}
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
            >
              <option value="">Toți agenții</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name} ({agent.email})
                </option>
              ))}
            </select>
          )}

          {/* 🔍 Căutare după client */}
          <div className="relative">
            <SearchIcon
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                darkMode ? "text-gray-300" : "text-gray-500"
              }`}
            />
            <input
              type="text"
              placeholder="Caută client..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-10 pr-4 py-2 w-96 border rounded-3xl shadow-sm transition-all ${
                darkMode
                  ? "bg-gray-800 text-white border-gray-600 placeholder-gray-400"
                  : "bg-white text-gray-900 border-gray-300 placeholder-gray-500"
              }`}
            />
          </div>
        </div>
      </div>

      {loading && <p className="text-center">🔄 Încărcare...</p>}
      {error && (
        <div className="bg-red-600 text-white text-center p-3 rounded-lg mb-4">
          ⚠️ {error}
        </div>
      )}

      {!loading && filteredCustomers.length === 0 && (
        <p className="text-center text-gray-400">
          ❌ Niciun rezultat găsit pentru căutare.
        </p>
      )}

      {!loading && filteredCustomers.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table
              className={`w-full border ${
                darkMode
                  ? "border-gray-700 bg-gray-900 text-gray-300"
                  : "border-gray-400 bg-white text-gray-800 shadow-lg"
              }`}
            >
              <thead>
                <tr
                  className={`${
                    darkMode
                      ? "bg-gray-700 text-white"
                      : "bg-gray-300 text-black"
                  }`}
                >
                  <th className="border p-2">#</th>
                  {isAdmin && <th className="border p-2">Agent</th>}
                  <th className="border p-2">Client</th>
                  <th className="border p-2">Total cumpărături</th>
                </tr>
              </thead>
              <tbody>
                {currentCustomers.map((customer, index) => (
                  <tr
                    key={customer.customerId}
                    className={`border ${
                      darkMode
                        ? index % 2 === 0
                          ? "bg-gray-800 hover:bg-gray-700"
                          : "bg-gray-900 hover:bg-gray-700"
                        : index % 2 === 0
                        ? "bg-gray-50 hover:bg-gray-200"
                        : "bg-white hover:bg-gray-200"
                    } transition text-center`}
                  >
                    <td className="border p-2">
                      {indexOfFirstCustomer + index + 1}
                    </td>
                    {isAdmin && (
                      <td className="border p-2">
                        {customer.agentName || "Fără agent"}
                      </td>
                    )}
                    <td className="border p-2">{customer.customerName}</td>
                    <td className="border p-2 font-semibold">
                      {customer.totalSpent.toLocaleString("ro-RO", {
                        style: "currency",
                        currency: "EUR",
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ✅ Componenta de paginare */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}
    </div>
  );
};

export default CustomerStatistics;
